/**
 * Variant Gallery Filter
 *
 * Slides for non-selected variants are pre-rendered server-side into
 * #VariantSlideBuffer-{sectionId} and #VariantThumbBuffer-{sectionId} divs
 * (outside the Flickity container), so Flickity never initialises with them
 * and there is no wrong-image flash on page load.
 *
 * On variant change, swaps slides between the buffer and the slider and
 * reinitialises Flickity via the native Product instance.
 *
 * Requires:
 *  - slides/thumbs with data-variant-media-id (product-images.liquid / media.liquid)
 *  - #VariantMediaJson-{sectionId} script tag (product-images.liquid)
 *  - #VariantSlideBuffer-{sectionId} and #VariantThumbBuffer-{sectionId} divs
 *  - sectionContainer._productInstance set in theme.js Product constructor
 */

(function () {
  'use strict';

  function VariantGalleryFilter(sectionContainer) {
    var sectionId = sectionContainer.dataset.sectionId;
    if (!sectionId) return;

    var jsonEl = document.getElementById('VariantMediaJson-' + sectionId);
    if (!jsonEl) return;

    var data;
    try { data = JSON.parse(jsonEl.textContent); } catch (e) { return; }

    if (!data.variantMedia || !Object.keys(data.variantMedia).length) return;

    this.sectionContainer  = sectionContainer;
    this.productId         = String(data.productId || '');
    this.variantMediaMap   = data.variantMedia;   // { "variantId": mediaId }
    this.currentVariantId  = String(data.currentVariantId);

    this.mainSlider    = sectionContainer.querySelector('[data-product-photos]');
    this.thumbScroller = sectionContainer.querySelector('.product__thumbs--scroller');

    if (!this.mainSlider) return;

    // Use server-rendered buffers so Flickity never initialises with
    // non-current-variant slides (eliminates the wrong-image flash).
    this.slideBuffer = document.getElementById('VariantSlideBuffer-' + sectionId);
    this.thumbBuffer = document.getElementById('VariantThumbBuffer-' + sectionId);

    if (!this.slideBuffer) {
      // Fallback for products with no variant-specific media.
      return;
    }

    // Slides were server-side rendered with sequential data-index values, but
    // re-index here as well so onSlideChange() lookups are always correct.
    var slides = this.mainSlider.querySelectorAll('.product-main-slide');
    slides.forEach(function (slide, i) {
      slide.setAttribute('data-index', i);
    });

    // Listen on the section container, not document — variantChange fires only
    // on the specific container that changed, so no cross-section leakage.
    sectionContainer.addEventListener('variantChange', function (evt) {
      if (!evt.detail || !evt.detail.variant) return;
      var newId = String(evt.detail.variant.id);
      if (newId === this.currentVariantId) return;
      this.filterGallery(newId);
    }.bind(this));
  }

  VariantGalleryFilter.prototype.filterGallery = function (variantId) {
    var targetMediaId = String(this.variantMediaMap[variantId] || '');

    // 1. Destroy Flickity BEFORE touching the DOM so it releases cell references cleanly.
    this.destroyFlickity();

    // 2. Bring everything back from the buffers.
    this.unstashAll();

    // 3. Park every variant slide that is NOT the target into the slide buffer.
    this.mainSlider.querySelectorAll('[data-variant-media-id]').forEach(function (slide) {
      if (slide.dataset.variantMediaId !== targetMediaId) {
        this.slideBuffer.appendChild(slide);
      }
    }.bind(this));

    if (this.thumbScroller && this.thumbBuffer) {
      this.thumbScroller.querySelectorAll('[data-variant-media-id]').forEach(function (thumb) {
        if (thumb.dataset.variantMediaId !== targetMediaId) {
          this.thumbBuffer.appendChild(thumb);
        }
      }.bind(this));
    }

    // 4. Move the active variant slide to position 0.
    var targetSlide = this.mainSlider.querySelector('[data-variant-media-id="' + targetMediaId + '"]');
    if (targetSlide) {
      this.mainSlider.insertBefore(targetSlide, this.mainSlider.firstChild);
    }

    // 5. Move the active variant thumb to position 0.
    if (this.thumbScroller) {
      var targetThumb = this.thumbScroller.querySelector('[data-variant-media-id="' + targetMediaId + '"]');
      if (targetThumb) {
        this.thumbScroller.insertBefore(targetThumb, this.thumbScroller.firstChild);
      }
    }

    // 6. Re-index data-index on slides so onSlideChange() lookups stay correct.
    var slides = this.mainSlider.querySelectorAll('.product-main-slide');
    slides.forEach(function (slide, i) {
      slide.setAttribute('data-index', i);
    });

    // 7. Fix starting-slide class so initProductSlider picks index 0.
    this.mainSlider.querySelectorAll('.starting-slide').forEach(function (s) {
      s.classList.remove('starting-slide');
    });
    if (slides[0]) slides[0].classList.add('starting-slide');

    // 8. Reinitialise Flickity.
    this.reinitSlider();
    this.currentVariantId = variantId;
  };

  VariantGalleryFilter.prototype.unstashAll = function () {
    while (this.slideBuffer.firstChild) {
      this.mainSlider.appendChild(this.slideBuffer.firstChild);
    }
    if (this.thumbScroller && this.thumbBuffer) {
      while (this.thumbBuffer.firstChild) {
        this.thumbScroller.appendChild(this.thumbBuffer.firstChild);
      }
    }
  };

  VariantGalleryFilter.prototype.destroyFlickity = function () {
    var p = this.sectionContainer._productInstance;
    if (p && p.flickity && typeof p.flickity.destroy === 'function') {
      p.flickity.destroy();
      p.flickity = null;
    }
  };

  VariantGalleryFilter.prototype.reinitSlider = function () {
    var p = this.sectionContainer._productInstance;
    if (!p || typeof p.initProductSlider !== 'function') return;
    p.settings.currentSlideIndex = 0;
    p.initProductSlider(p.variants && p.variants.currentVariant);
  };

  // -------------------------------------------------------------------------

  // Always defer until after theme.js DOMContentLoaded has run (which
  // instantiates Product objects and sets _productInstance).
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-section-type="product"]').forEach(function (el) {
      new VariantGalleryFilter(el);
    });
  });

})();
